import React, { useEffect, useMemo, useState } from "react";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ArrowUpDown, Columns3, Group, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGetProcessedDataQuery, useGetTabRowsQuery } from "@/services/tax-closing";
import { formatBRL } from "@/utils/money";
import { StateBadge } from "./state-badge";
import { useSituationFilter } from "../hooks/use-situation-filter";
import { SituationFilter } from "../components/situation-filter";

type CellType = "text" | "mono" | "badge" | "uf" | "money" | "pct" | "money-highlight" | "dif" | "situation";
type CellHighlight = "yellow" | "green" | null;

interface ColDef {
  key: string;
  label: string;
  type: CellType;
  bg?: CellHighlight;
}

/**
 * Todas as 78 colunas da aba CONFERENCIAS da planilha de referência
 * (mapeamento-gft/Amostragem planilha de saídas.xlsx) na ordem A→BZ.
 *
 * Cores de destaque:
 *   green (#92D050) → Modelo (coluna F)
 *   yellow (#FFFF00) → Alíq. ICMS (AP), NOVA BASE ICMS (BX), NOVO ICMS (BY), DIF (BZ)
 *
 * NÃO reordenar/remover/renomear sem combinar com o time fiscal — a planilha
 * é o contrato de referência e o fiscal espera ver colunas na mesma posição.
 */
const VISIBLE_COLUMNS: ColDef[] = [
  // A–E
  { key: "empresa", label: "Empresa", type: "text" },
  { key: "cnpjEmpresa", label: "CNPJ Empresa", type: "mono" },
  { key: "razaoSocial", label: "Razão Social", type: "text" },
  { key: "usuarioDigitador", label: "Usuário Digitador", type: "text" },
  { key: "dataProcesso", label: "Data Processo", type: "text" },
  // F (green)
  { key: "modelo", label: "Modelo", type: "badge", bg: "green" },
  // G–J
  { key: "serie", label: "Série", type: "mono" },
  { key: "numNota", label: "Núm. Nota", type: "text" },
  { key: "naturezaOperacao", label: "Natureza Operação", type: "text" },
  { key: "numProcesso", label: "Núm. Processo", type: "text" },
  // K–N
  { key: "dataEmissao", label: "Data Emissão", type: "text" },
  { key: "mesCompetencia", label: "Mês Competência", type: "text" },
  { key: "emissaoPropria", label: "Emissão Própria", type: "text" },
  { key: "entradaSaida", label: "Entrada / Saída", type: "text" },
  // O–R
  { key: "tipoNota", label: "Tipo Nota", type: "text" },
  { key: "situacao", label: "Situação", type: "situation" },
  { key: "situacaoDocumento", label: "Situação Documento", type: "text" },
  { key: "situacaoTitulo", label: "Situação Título", type: "text" },
  // S–X
  { key: "cnpjFornecCliente", label: "CNPJ Fornec/Cliente", type: "mono" },
  { key: "fornecedorCliente", label: "Fornecedor/Cliente", type: "text" },
  { key: "ufFornCliente", label: "UF Forn.Cliente", type: "uf" },
  { key: "municipioFornCliente", label: "Município Forn./Cliente", type: "text" },
  { key: "ufExecucaoDeServico", label: "UF Execução de Serviço", type: "text" },
  { key: "municipioExecucaoDeServico", label: "Município Execução de Serviço", type: "text" },
  // Y–Z
  { key: "chaveDeAcesso", label: "Chave de Acesso", type: "mono" },
  { key: "valorNota", label: "Valor Nota", type: "money" },
  // AB (observações — pulamos AA "STATUS" que não existe no GFT)
  { key: "observacoes", label: "Observações", type: "text" },
  // AC–AL
  { key: "cfop", label: "CFOP", type: "mono" },
  { key: "descricaoCfop", label: "Descrição CFOP", type: "text" },
  { key: "codMaterial", label: "Cod material", type: "text" },
  { key: "qtdItem", label: "Qtd. Item", type: "text" },
  { key: "vlrItem", label: "Vlr. Item", type: "money" },
  { key: "vlrTotal", label: "Vlr. Total", type: "money" },
  { key: "vlrFrete", label: "Vlr. Frete", type: "money" },
  { key: "vlrSeguro", label: "Vlr. Seguro", type: "money" },
  { key: "vlrDesconto", label: "Vlr. Desconto", type: "money" },
  { key: "vlrOutrasDespesas", label: "Vlr. Outras Despesas", type: "money" },
  // AM–AO
  { key: "cstIcms", label: "CST ICMS", type: "mono" },
  { key: "pctReducaoIcms", label: "% Redução ICMS", type: "pct" },
  { key: "baseIcms", label: "Base ICMS", type: "money" },
  // AP (yellow)
  { key: "aliqIcms", label: "Alíq. ICMS", type: "pct", bg: "yellow" },
  // AQ–AY
  { key: "vlrIcmsDiferido", label: "Vlr. ICMS Diferido", type: "money" },
  { key: "valorIcms", label: "Valor ICMS", type: "money" },
  { key: "vlrBaseIcmsEstimativa", label: "Vlr Base ICMS Estimativa", type: "money" },
  { key: "pctCargaMediaIcms", label: "Pct Carga Média ICMS", type: "pct" },
  { key: "pctCargaMediaFcp", label: "Pct Carga Média FCP", type: "pct" },
  { key: "vlrIcmsEstimativa", label: "Vlr ICMS Estimativa", type: "money" },
  { key: "vlrBaseIcmsSt", label: "Vlr Base ICMS ST", type: "money" },
  { key: "pctAliqIcmsSt", label: "Pct Alíq ICMS ST", type: "pct" },
  { key: "vlrIcmsSt", label: "Vlr ICMS ST", type: "money" },
  // AZ–BF
  { key: "baseFcp", label: "Base FCP", type: "money" },
  { key: "aliqFcp", label: "Alíq. FCP", type: "pct" },
  { key: "valorFcp", label: "Valor FCP", type: "money" },
  { key: "pctReducaoIcmsSt", label: "% Redução ICMS ST", type: "pct" },
  { key: "baseFcpSt", label: "Base FCP ST", type: "money" },
  { key: "aliqFcpSt", label: "Alíq. FCP ST", type: "pct" },
  { key: "valorFcpSt", label: "Valor FCP ST", type: "money" },
  // BG–BJ
  { key: "cstIpi", label: "CST IPI", type: "mono" },
  { key: "baseIpi", label: "Base IPI", type: "money" },
  { key: "aliqIpi", label: "Alíq. IPI", type: "pct" },
  { key: "valorIpi", label: "Valor IPI", type: "money" },
  // BK–BQ
  { key: "cstPisCofins", label: "CST PIS/COFINS", type: "mono" },
  { key: "basePis", label: "Base PIS", type: "money" },
  { key: "aliqPis", label: "Alíq. PIS", type: "pct" },
  { key: "valorPis", label: "Valor PIS", type: "money" },
  { key: "baseCofins", label: "Base COFINS", type: "money" },
  { key: "aliqCofins", label: "Alíq. COFINS", type: "pct" },
  { key: "valorCofins", label: "Valor COFINS", type: "money" },
  // BR–BU
  { key: "outrasDespesas", label: "Outras Despesas", type: "money" },
  { key: "outrosIpi", label: "Outros IPI", type: "text" },
  { key: "outrosIcms", label: "Outros ICMS", type: "text" },
  { key: "icmsIsento", label: "ICMS Isento", type: "text" },
  // BV–BW
  { key: "descricaoMaterial", label: "Descrição Material", type: "text" },
  { key: "codigoNcm", label: "Código NCM", type: "mono" },
  // BX–BZ (yellow — colunas calculadas)
  { key: "novaBaseIcms", label: "NOVA BASE ICMS", type: "money-highlight", bg: "yellow" },
  { key: "novoIcms", label: "NOVO ICMS", type: "money-highlight", bg: "yellow" },
  { key: "dif", label: "DIF", type: "dif", bg: "yellow" },
];

const SITUATION_BADGE_CLASS: Record<string, string> = {
  Autorizada: "border-green-300 bg-green-50 text-green-700",
  Cancelada: "border-red-300 bg-red-50 text-red-700",
};
const SITUATION_BADGE_FALLBACK = "border-muted bg-muted/40 text-muted-foreground";

const BG_CLASS: Record<NonNullable<CellHighlight>, string> = {
  yellow: "bg-yellow-50",
  green: "bg-green-50",
};

const BG_HEADER_CLASS: Record<NonNullable<CellHighlight>, string> = {
  yellow: "bg-yellow-100/60",
  green: "bg-green-100/60",
};

const ALL_KEYS = new Set(VISIBLE_COLUMNS.map((c) => c.key));

interface ColumnPreset {
  id: string;
  label: string;
  keys: Set<string>;
}

const COLUMN_PRESETS: ColumnPreset[] = [
  {
    id: "all",
    label: "Todas (78)",
    keys: ALL_KEYS,
  },
  {
    id: "resumo",
    label: "Resumo",
    keys: new Set([
      "empresa", "modelo", "numNota", "situacao", "cfop", "descricaoCfop", "cstIcms",
      "ufFornCliente", "dataEmissao", "valorNota", "vlrTotal", "vlrDesconto",
      "baseIcms", "aliqIcms", "valorIcms", "novaBaseIcms", "novoIcms", "dif",
    ]),
  },
  {
    id: "icms",
    label: "ICMS",
    keys: new Set([
      "empresa", "numNota", "cfop", "cstIcms", "baseIcms", "aliqIcms",
      "valorIcms", "pctReducaoIcms", "vlrIcmsDiferido", "vlrBaseIcmsSt",
      "pctAliqIcmsSt", "vlrIcmsSt", "novaBaseIcms", "novoIcms", "dif",
    ]),
  },
  {
    id: "calculadas",
    label: "Calculadas",
    keys: new Set([
      "empresa", "numNota", "cfop", "vlrTotal", "vlrDesconto",
      "aliqIcms", "valorIcms", "novaBaseIcms", "novoIcms", "dif",
    ]),
  },
  {
    id: "notas",
    label: "Notas",
    keys: new Set([
      "empresa", "modelo", "serie", "numNota", "dataEmissao", "situacao",
      "chaveDeAcesso", "valorNota", "cfop", "descricaoCfop",
      "cnpjFornecCliente", "fornecedorCliente", "ufFornCliente",
    ]),
  },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 20;

interface AuditTableProps {
  jobId: string;
}

export function AuditTable({ jobId }: AuditTableProps) {
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(
    COLUMN_PRESETS[1].keys, // default = Resumo (17 cols)
  );
  const [activePreset, setActivePreset] = useState<string>("resumo");
  const [isGrouped, setIsGrouped] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const visibleColumns = useMemo(
    () => VISIBLE_COLUMNS.filter((c) => visibleKeys.has(c.key)),
    [visibleKeys],
  );

  const applyPreset = (preset: ColumnPreset) => {
    setVisibleKeys(new Set(preset.keys));
    setActivePreset(preset.id);
  };

  const toggleColumn = (key: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size <= 1) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    setActivePreset("custom");
  };

  // Debounce search to avoid hammering the backend on every keystroke
  const searchTimerRef = useMemo(() => ({ id: null as ReturnType<typeof setTimeout> | null }), []);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.id) clearTimeout(searchTimerRef.id);
    searchTimerRef.id = setTimeout(() => {
      setSearchDebounced(value);
      setPage(1);
    }, 400);
  };

  const { situation, filters, setSituation } = useSituationFilter();

  useEffect(() => {
    setPage(1);
  }, [situation]);

  const { data: processedData } = useGetProcessedDataQuery(
    { jobId, filters },
    { skip: !jobId },
  );
  const situationOptions = useMemo(
    () => processedData?.facets?.situacao ?? [],
    [processedData],
  );

  const { data, isFetching } = useGetTabRowsQuery({
    jobId,
    tab: "conferencias",
    page,
    limit: pageSize,
    sort: sortKey ?? undefined,
    order: sortAsc ? "asc" : "desc",
    search: searchDebounced || undefined,
    filters,
  });

  const paged = (data?.rows ?? []) as Record<string, any>[];
  const meta = data?.meta;
  const totalPages = meta?.totalPages ?? 1;

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
    setPage(1);
  };

  const toggleExpand = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Nota-level fields (identical across items of the same NF-e).
  // Item-level fields vary per row.
  const NOTA_LEVEL_KEYS = new Set([
    "empresa", "cnpjEmpresa", "razaoSocial", "usuarioDigitador", "dataProcesso",
    "modelo", "serie", "numNota", "naturezaOperacao", "numProcesso",
    "dataEmissao", "mesCompetencia", "emissaoPropria", "entradaSaida",
    "tipoNota", "situacao", "situacaoDocumento", "situacaoTitulo",
    "cnpjFornecCliente", "fornecedorCliente", "ufFornCliente",
    "municipioFornCliente", "chaveDeAcesso", "valorNota", "observacoes",
  ]);

  const grouped = useMemo(() => {
    if (!isGrouped) return null;
    const map = new Map<string, Record<string, any>[]>();
    for (const row of paged) {
      const key = String(row.chaveDeAcesso ?? row.numNota ?? "");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    }
    return Array.from(map.entries()).map(([key, items]) => ({
      key,
      headerRow: items[0],
      items,
    }));
  }, [paged, isGrouped]);

  const renderCell = (col: ColDef, val: any) => {
    const str = String(val ?? "");
    const num = parseFloat(str.replace(",", "."));

    const renderers: Record<CellType, () => JSX.Element | string> = {
      uf: () =>
        str ? <StateBadge uf={str} /> : <span className="text-muted-foreground">—</span>,
      badge: () => (
        <Badge variant="outline" className="font-mono text-xs">
          {str}
        </Badge>
      ),
      situation: () =>
        str ? (
          <Badge
            variant="outline"
            className={cn(
              "text-xs",
              SITUATION_BADGE_CLASS[str] ?? SITUATION_BADGE_FALLBACK,
            )}
          >
            {str}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      mono: () => <span className="font-mono text-xs">{str || "—"}</span>,
      money: () => <span className="tabular-nums">{formatBRL(str)}</span>,
      "money-highlight": () => (
        <span className="tabular-nums font-semibold">{formatBRL(str)}</span>
      ),
      pct: () => <span className="tabular-nums">{str ? `${str}%` : "—"}</span>,
      dif: () => (
        <span
          className={cn(
            "tabular-nums font-semibold",
            !isNaN(num) && num > 0 && "text-red-600",
            !isNaN(num) && num < 0 && "text-green-600",
            !isNaN(num) && num === 0 && "text-muted-foreground",
          )}
        >
          {formatBRL(str)}
        </span>
      ),
      text: () => str,
    };

    return renderers[col.type]();
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Preset pills + column toggler */}
      <div className="flex items-center gap-2 flex-wrap">
        {COLUMN_PRESETS.map((preset) => (
          <Button
            key={preset.id}
            variant={activePreset === preset.id ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => applyPreset(preset)}
          >
            {preset.label}
          </Button>
        ))}
        <Button
          variant={isGrouped ? "default" : "outline"}
          size="sm"
          className="h-7 text-xs gap-1.5"
          onClick={() => {
            setIsGrouped(!isGrouped);
            setExpandedGroups(new Set());
          }}
        >
          <Group className="h-3.5 w-3.5" />
          Agrupar NF-e
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5">
              <Columns3 className="h-3.5 w-3.5" />
              Colunas ({visibleKeys.size}/{VISIBLE_COLUMNS.length})
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Buscar coluna..." />
              <CommandList>
                <CommandEmpty>Nenhuma coluna encontrada.</CommandEmpty>
                <CommandGroup>
                  {VISIBLE_COLUMNS.map((col) => (
                    <CommandItem
                      key={col.key}
                      value={col.label}
                      onSelect={() => toggleColumn(col.key)}
                      className="gap-2 text-xs"
                    >
                      <Check
                        className={cn(
                          "h-3.5 w-3.5 shrink-0",
                          visibleKeys.has(col.key) ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {col.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar em todas as colunas..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        {situationOptions.length > 0 && (
          <SituationFilter
            situations={situationOptions}
            value={situation}
            onChange={setSituation}
          />
        )}
      </div>

      <div className="relative overflow-x-auto border rounded-lg">
        {isFetching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="border-b bg-muted/40">
              {visibleColumns.map((col) => {
                const isSorted = sortKey === col.key;
                const SortIcon = isSorted ? (sortAsc ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <th
                    key={col.key}
                    className={cn(
                      "py-2.5 px-3 text-left font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/60 select-none transition-colors",
                      col.bg && BG_HEADER_CLASS[col.bg],
                      isSorted ? "text-foreground" : "text-muted-foreground",
                    )}
                    onClick={() => toggleSort(col.key)}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      <SortIcon className={cn("h-3 w-3", isSorted ? "opacity-100" : "opacity-30")} />
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isGrouped && grouped ? (
              <>
                {grouped.map((group, gIdx) => {
                  const isExpanded = expandedGroups.has(group.key);
                  return (
                    <React.Fragment key={group.key || gIdx}>
                      {/* Header row — nota-level summary */}
                      <tr
                        className={cn(
                          "border-b transition-colors cursor-pointer hover:bg-muted/30",
                          gIdx % 2 === 0 ? "bg-background" : "bg-muted/10",
                        )}
                        onClick={() => toggleExpand(group.key)}
                      >
                        {visibleColumns.map((col, cIdx) => (
                          <td
                            key={col.key}
                            className={cn("py-2.5 px-3", col.bg && BG_CLASS[col.bg])}
                          >
                            {cIdx === 0 ? (
                              <div className="flex items-center gap-2">
                                {isExpanded
                                  ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                                  : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                }
                                {renderCell(col, group.headerRow[col.key])}
                                <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 h-5">
                                  {group.items.length} {group.items.length === 1 ? "item" : "itens"}
                                </Badge>
                              </div>
                            ) : (
                              NOTA_LEVEL_KEYS.has(col.key)
                                ? renderCell(col, group.headerRow[col.key])
                                : <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                      {/* Expanded item rows */}
                      {isExpanded && group.items.map((item, iIdx) => (
                        <tr
                          key={`${group.key}-${iIdx}`}
                          className="border-b bg-muted/5 transition-colors hover:bg-muted/15"
                        >
                          {visibleColumns.map((col, cIdx) => (
                            <td
                              key={col.key}
                              className={cn(
                                "py-2 px-3 text-xs",
                                col.bg && BG_CLASS[col.bg],
                                cIdx === 0 && "pl-10",
                              )}
                            >
                              {renderCell(col, item[col.key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {grouped.length === 0 && !isFetching && (
                  <tr>
                    <td colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                      Nenhum registro encontrado
                    </td>
                  </tr>
                )}
              </>
            ) : (
              <>
                {paged.map((row, idx) => (
                  <tr
                    key={idx}
                    className={cn(
                      "border-b transition-colors hover:bg-muted/20",
                      idx % 2 === 0 ? "bg-background" : "bg-muted/10",
                    )}
                  >
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        className={cn("py-2.5 px-3", col.bg && BG_CLASS[col.bg])}
                      >
                        {renderCell(col, row[col.key])}
                      </td>
                    ))}
                  </tr>
                ))}
                {paged.length === 0 && !isFetching && (
                  <tr>
                    <td colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                      Nenhum registro encontrado
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-3 text-muted-foreground">
          <span>
            {isFetching ? "Carregando..." : (
              <>
                {meta?.totalFiltered?.toLocaleString("pt-BR") ?? 0} de {meta?.total?.toLocaleString("pt-BR") ?? 0} registro{(meta?.total ?? 0) !== 1 ? "s" : ""} &middot; Página {page} de {Math.max(1, totalPages)}
              </>
            )}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-xs">Itens:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" disabled={page <= 1 || isFetching} onClick={() => setPage(page - 1)}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages || isFetching} onClick={() => setPage(page + 1)}>
            Próximo <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
